import { Link } from 'react-router-dom';
import { InvoiceQualitySummary } from '../../utils/dataQuality';

interface InvoiceHeaderProps {
  invoiceNumber?: string;
  publicToken?: string;
  qualitySummary?: InvoiceQualitySummary;
  reconciliationStatus?: 'matched' | 'discrepancy' | 'pending';
}

export default function InvoiceHeader({
  invoiceNumber,
  publicToken,
  qualitySummary,
  reconciliationStatus = 'matched',
}: InvoiceHeaderProps) {
  return (
    <div className="flex flex-col gap-4 mb-6">
      {/* Top Nav Line */}
      <div className="flex items-center justify-between">
        <Link
          to="/invoices"
          className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-brand-vib hover:text-brand-vib-hover dark:text-brand-light transition-colors"
        >
          <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
          Back to all invoices
        </Link>

        {/* Monospace Eyebrow */}
        <div className="text-[11.5px] font-mono font-semibold tracking-wider text-brand-vib uppercase dark:text-brand-light">
          / INVOICE ANALYSIS
        </div>
      </div>

      {/* Main Title & Status Row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl sm:text-3xl font-bold text-brand-navy dark:text-white tracking-tight">
            Results
          </h1>
          {invoiceNumber && (
            <span className="text-sm font-mono text-brand-soft dark:text-bodydark2">
              #{invoiceNumber}
            </span>
          )}
          {publicToken && qualitySummary && (
            <Link
              to={`/invoices/${publicToken}/issues`}
              title="View Data Quality Issues"
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono font-bold tracking-wide transition-all shadow-xs ${
                qualitySummary.badgeType === 'critical'
                  ? 'bg-rose-500 text-white hover:bg-rose-600'
                  : qualitySummary.badgeType === 'warning'
                  ? 'bg-amber-500 text-white hover:bg-amber-600'
                  : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100'
              }`}
            >
              {qualitySummary.badgeType === 'critical' && <span>🔴</span>}
              {qualitySummary.badgeType === 'warning' && <span>⚠️</span>}
              {qualitySummary.badgeType === 'clean' && <span>✓</span>}
              <span>
                {qualitySummary.totalIssues > 0
                  ? `${qualitySummary.totalIssues} ${qualitySummary.totalIssues === 1 ? 'ISSUE' : 'ISSUES'}`
                  : '0 ISSUES'}
              </span>
            </Link>
          )}
        </div>

        {/* Reconciliation Status Badges */}
        <div className="flex items-center gap-2">
          {reconciliationStatus === 'matched' && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono font-semibold bg-brand-green-tint text-[#0a6b4a] dark:bg-emerald-950/40 dark:text-emerald-300 border border-brand-green/45 dark:border-emerald-800">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-green" />
              PO RECONCILED (100%)
            </span>
          )}
          <span className="px-3 py-1 rounded-full text-xs font-mono font-semibold bg-brand-blue-tint text-brand-vib dark:bg-brand-900/40 dark:text-brand-300 border border-brand-vib/20">
            AI AUDIT COMPLETE
          </span>
        </div>
      </div>

      {/* AI Verification Callout Alert */}
      <div className="flex items-start gap-3 p-3.5 rounded-2xl bg-brand-blue-tint dark:bg-brand-900/20 border border-brand-vib/15 dark:border-brand-800 text-xs text-[#3b4569] dark:text-bodydark leading-relaxed">
        <svg className="w-4 h-4 text-brand-vib flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>
          <strong>AI Extraction Notice:</strong> Extracted values are verified by OCR & LLM neural validation. You can inspect confidence levels, review line items, or edit field values using the quick actions rail.
        </span>
      </div>
    </div>
  );
}
